import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Alert,
  LinearProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Slider,
  Tooltip,
  Collapse,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PublishIcon from '@mui/icons-material/Publish';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import LinkIcon from '@mui/icons-material/Link';
import LockClockIcon from '@mui/icons-material/LockClock';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DescriptionIcon from '@mui/icons-material/Description';

// Contexts and Components
import { useReport, REPORT_STATUS } from '../../contexts/ReportContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  useShare,
  SHARE_DURATION,
  SHARE_DURATION_LABELS,
  LINK_EXPIRATION,
  LINK_EXPIRATION_LABELS,
} from '../../contexts/ShareContext';
import { PageHeaderCard } from '../../components/common';

// Utils and Constants
import { getProjectStatusColor, getProjectStatusLabel, formatDate, formatDateTime } from '../../utils/helper';

const getActionColor = (action) => {
  switch (action) {
    case 'created':
      return 'primary.main';
    case 'entry_added':
      return 'success.main';
    case 'entry_updated':
    case 'entry_corrected':
      return 'warning.main';
    case 'published':
      return 'info.main';
    default:
      return 'grey.500';
  }
};

const getActionLabel = (action) => {
  switch (action) {
    case 'created':
      return 'Report Created';
    case 'entry_added':
      return 'Entry Added';
    case 'entry_updated':
      return 'Entry Updated';
    case 'entry_corrected':
      return 'Entry Corrected';
    case 'published':
      return 'Report Published';
    default:
      return action;
  }
};

const ReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getReportById, updateReportEntry } = useReport();
  const { generateShareLink } = useShare();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [editForm, setEditForm] = useState({
    status: '',
    progress: 0,
    notes: '',
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDuration, setShareDuration] = useState(SHARE_DURATION.LATEST);
  const [shareExpiration, setShareExpiration] = useState(LINK_EXPIRATION.ONE_WEEK);
  const [generatedLink, setGeneratedLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  const report = getReportById(id);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!report) return { totalEntries: 0, avgProgress: 0, completed: 0 };
    const totalEntries = report.entries.length;
    const avgProgress = totalEntries > 0
      ? Math.round(report.entries.reduce((sum, e) => sum + e.progress, 0) / totalEntries)
      : 0;
    const completed = report.entries.filter((e) => e.status === 'completed').length;
    return { totalEntries, avgProgress, completed };
  }, [report]);

  if (!report) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/reports/history')}
          sx={{ mb: 2 }}
        >
          Back to History
        </Button>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <DescriptionIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Report Not Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The report you are looking for does not exist or has been removed.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const handleBack = () => {
    navigate('/reports/history');
  };

  const handleOpenEditDialog = (entry) => {
    setEditingEntry(entry);
    setEditForm({
      status: entry.status,
      progress: entry.progress,
      notes: entry.notes,
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingEntry(null);
  };

  const handleSaveEdit = () => {
    if (!editingEntry) return;

    updateReportEntry(report.id, editingEntry.id, editForm, user?.name || 'Unknown');
    handleCloseEditDialog();
  };

  const getReportStatusChip = (status) => {
    switch (status) {
      case REPORT_STATUS.PUBLISHED:
        return <Chip label="Published" color="success" size={isMobile ? 'small' : 'medium'} icon={<CheckIcon sx={{ fontSize: isMobile ? 14 : 18 }} />} />;
      case REPORT_STATUS.DRAFT:
        return <Chip label="Draft" color="warning" size={isMobile ? 'small' : 'medium'} icon={<EditIcon sx={{ fontSize: isMobile ? 14 : 18 }} />} />;
      case REPORT_STATUS.ARCHIVED:
        return <Chip label="Archived" color="default" size={isMobile ? 'small' : 'medium'} icon={<HistoryIcon sx={{ fontSize: isMobile ? 14 : 18 }} />} />;
      default:
        return <Chip label={status} size={isMobile ? 'small' : 'medium'} />;
    }
  };

  const handleOpenShareDialog = () => {
    setGeneratedLink('');
    setLinkCopied(false);
    setShareDialogOpen(true);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setGeneratedLink('');
    setLinkCopied(false);
  };

  const handleGenerateLink = () => {
    const link = generateShareLink({
      reportIds: [report.id],
      duration: shareDuration,
      expirationDays: shareExpiration,
      createdBy: user?.name || 'Unknown',
    });
    setGeneratedLink(link);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const progressMarks = [
    { value: 0, label: '0%' },
    { value: 25, label: '25%' },
    { value: 50, label: '50%' },
    { value: 75, label: '75%' },
    { value: 100, label: '100%' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
          color="inherit"
        >
          Back to History
        </Button>
        <PageHeaderCard
          mb={0}
          title="Report Detail"
          subtitle="View and manage report entries and corrections"
          titleSuffix={getReportStatusChip(report.status)}
          actions={(
            <Button
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={handleOpenShareDialog}
            >
              Share Report
            </Button>
          )}
        />
      </Box>

      {/* Report Info Card */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1.5,
              bgcolor: 'primary.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CalendarMonthIcon sx={{ color: 'primary.main', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {formatDate(report.weekStart)} - {formatDate(report.weekEnd)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Week Period
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PublishIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Published At
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {report.publishedAt ? formatDateTime(report.publishedAt) : 'Not published'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Entries
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {stats.totalEntries} entries
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Avg. Progress
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {stats.avgProgress}%
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Completed
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {stats.completed} of {stats.totalEntries}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <AssignmentIcon color="primary" sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="h5" fontWeight={700}>
                {stats.totalEntries}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Entries
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <TrendingUpIcon color="info" sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="h5" fontWeight={700}>
                {stats.avgProgress}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg. Progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <CheckIcon color="success" sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="h5" fontWeight={700}>
                {stats.completed}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <HistoryIcon color="warning" sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="h5" fontWeight={700}>
                {report.auditLog.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Audit Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Report Entries */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AssignmentIcon color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Report Entries
            </Typography>
            <Chip label={`${stats.totalEntries} entries`} size={isMobile ? 'small' : 'medium'} variant="outlined" />
          </Box>
        </Box>

        {report.entries.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <AssignmentIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">
              No entries in this report
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Project / Task</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Updated</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.entries.map((entry) => (
                  <TableRow
                    key={entry.id}
                    hover
                    sx={{ '&:last-child td': { borderBottom: 0 } }}
                  >
                    <TableCell>
                      <Box>
                        <Typography fontWeight={500}>{entry.projectName}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {entry.taskName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getProjectStatusLabel(entry.status)}
                        color={getProjectStatusColor(entry.status)}
                        size={isMobile ? 'small' : 'medium'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={entry.progress}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                bgcolor: entry.progress === 100 ? 'success.main' : 'primary.main',
                              },
                            }}
                          />
                        </Box>
                        <Typography variant="body2" fontWeight={500} sx={{ minWidth: 40 }}>
                          {entry.progress}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={entry.notes || '-'} arrow placement="top">
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {entry.notes || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2">{formatDateTime(entry.updatedAt)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            by {entry.updatedBy}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit / Correct" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditDialog(entry)}
                          sx={{
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'primary.50' },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Audit Log */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            p: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={() => setShowAuditLog(!showAuditLog)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <HistoryIcon color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Audit Log
            </Typography>
            <Chip label={`${report.auditLog.length} events`} size={isMobile ? 'small' : 'medium'} variant="outlined" />
          </Box>
          <IconButton size="small">
            {showAuditLog ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={showAuditLog}>
          <Divider />
          <Box sx={{ p: 3 }}>
            <Stepper orientation="vertical" sx={{ '& .MuiStepConnector-line': { minHeight: 20 } }}>
              {report.auditLog.map((log, index) => (
                <Step key={index} active expanded>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: getActionColor(log.action),
                        }}
                      />
                    )}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography fontWeight={500}>{getActionLabel(log.action)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(log.timestamp)}
                      </Typography>
                    </Box>
                  </StepLabel>
                  <StepContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {log.user}
                      </Typography>
                    </Box>
                    {log.details && (
                      <Typography variant="caption" color="text.secondary">
                        {log.details}
                      </Typography>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Collapse>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="inherit" />
            <Typography variant="h6" color="inherit">Edit Entry (Correction)</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, mt: 1 }}>
            Changes will be recorded in the audit log for transparency.
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Read-only fields */}
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Project
                  </Typography>
                  <Typography fontWeight={500}>{editingEntry?.projectName || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Task
                  </Typography>
                  <Typography fontWeight={500}>{editingEntry?.taskName || '-'}</Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Editable fields */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.status}
                label="Status"
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <MenuItem value="not_started">Not Started</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Progress: {editForm.progress}%
              </Typography>
              <Box sx={{ px: 1 }}>
                <Slider
                  value={editForm.progress}
                  onChange={(e, val) => setEditForm({ ...editForm, progress: val })}
                  marks={progressMarks}
                  step={5}
                  min={0}
                  max={100}
                  valueLabelDisplay="auto"
                  sx={{
                    '& .MuiSlider-markLabel': {
                      fontSize: 11,
                    },
                  }}
                />
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Notes"
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              multiline
              rows={3}
              placeholder="Enter any notes or comments about this task..."
            />
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEditDialog} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" startIcon={<CheckIcon />}>
            Save Correction
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={handleCloseShareDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShareIcon color="inherit" />
            <Typography variant="h6" color="inherit">Share Report</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Report Info */}
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarMonthIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" fontWeight={500}>
                  {formatDate(report.weekStart)} - {formatDate(report.weekEnd)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {stats.totalEntries} entries
              </Typography>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Access Duration</InputLabel>
              <Select
                value={shareDuration}
                label="Access Duration"
                onChange={(e) => setShareDuration(e.target.value)}
                startAdornment={
                  <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                }
              >
                {Object.entries(SHARE_DURATION_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Link Expiration</InputLabel>
              <Select
                value={shareExpiration}
                label="Link Expiration"
                onChange={(e) => setShareExpiration(Number(e.target.value))}
                startAdornment={
                  <LockClockIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                }
              >
                {Object.entries(LINK_EXPIRATION_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={Number(key)}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {!generatedLink && (
              <Button
                variant="contained"
                onClick={handleGenerateLink}
                startIcon={<LinkIcon />}
                size="large"
              >
                Generate Share Link
              </Button>
            )}

            {generatedLink && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Share Link:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    value={generatedLink}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{ bgcolor: 'grey.50' }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleCopyLink}
                    startIcon={linkCopied ? <CheckIcon /> : <ContentCopyIcon />}
                    color={linkCopied ? 'success' : 'primary'}
                    sx={{ minWidth: 100 }}
                  >
                    {linkCopied ? 'Copied' : 'Copy'}
                  </Button>
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Anyone with this link can view the report without logging in.
                </Alert>
              </Box>
            )}
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseShareDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportDetail;
